/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import LayoutAuthenticated from "@/src/containers/Layouts/LayoutAuthenticated";
import HintBox from "../../../../components/Box/HintBox/HintBox";
import LegalbirdLoader from "../../../../components/ContentLoader/LegalbirdLoader";
import { Link, Navigate } from "react-router";
import Button from "@mui/material/Button";
import { getCaseLink, getProductData, getProductNameBy } from "@/src/services/productService";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import _cloneDeep from "lodash/cloneDeep";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "../../../../services/reactQuery/reactQueryService";
import { useInquiryToCustomers, useInvalidateInquiryToCustomer } from "@/src/hooks/queries/inquiryToCustomer";
import useProductParams from "../../../../hooks/useProductParams";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import { STATUS_ADMINISTRATIVE_DECISION_ISSUED } from "@/src/services/administrativeLawLitigationCase/leadStatus";
import AdministrativeDecisionFeedbackForm
  from "../../../../components/Form/AbstractAdministrativeLawLitigationCase/AdministrativeDecisionFeedbackForm/AdministrativeDecisionFeedbackForm";

const AdministrativeDecisionFeedbackPage = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId)
  });
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const queryClient = useQueryClient();
  const { invalidateInquiryToCustomer } = useInvalidateInquiryToCustomer();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data);
      await invalidateInquiryToCustomer();
    },
  });

  const inquiryFilters = {
    identifier: "administrativeDecisionFeedbackInquiry",
    productId: productId,
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
    "status[2]": InquiryToCustomerStatus.STATUS_IN_PROGRESS,
  };
  const { data: inquiries } = useInquiryToCustomers(inquiryFilters, { enabled: !!product });

  if (isLoading || !product || !inquiries) {
    return (
      <LayoutAuthenticated>
        <LegalbirdLoader />
      </LayoutAuthenticated>
    );
  }

  if (!!product.administrativeDecisionStatusClient || product.leadStatus !== STATUS_ADMINISTRATIVE_DECISION_ISSUED) {
    return (
      <LayoutAuthenticated>
        <HintBox headline={"Vielen Dank für Ihre Rückmeldung!"}>
          Wir haben Ihre Rückmeldung erhalten und werden Ihren Fall entsprechend weiter bearbeiten. Sie werden über das weitere Vorgehen wie gewohnt per
          Nachricht und Ihre Servicewelt informiert.
          <Button component={Link} to={getCaseLink(product)} variant={"text"} color={"primary"}>
            Zurück zum Fall
          </Button>
        </HintBox>
      </LayoutAuthenticated>
    );
  }

  const currentInquiryToCustomer = inquiries[0];

  if (!currentInquiryToCustomer || currentInquiryToCustomer.identifier !== "administrativeDecisionFeedbackInquiry") {
    return <Navigate to={getCaseLink(product)} />;
  }

  const getHintText = (product: AbstractCase) => {
    return (
      <p>
        <strong>
          {product.administrativeProceedingsLetterSentDate !== null
            ? "Die Behörde hat auf unser Schreiben mit einem Bescheid reagiert."
            : "zu Ihrem Verfahren liegt ein Bescheid der Behörde vor."}
        </strong>
        <br />
        <br />
        Wir benötigen als nächstes Ihre Rückmeldung darüber, wie Sie weiter vorgehen möchten.
      </p>
    );
  };

  const initialValues = {
    administrativeDecisionStatusClient: null,
    administrativeDecisionFeedbackClient: null,
    additionalCustomerNote: "",
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    setIsLoadingInternal(true);

    const updateData = _cloneDeep(values);

    if (updateData.additionalCustomerNote === "") {
      updateData.additionalCustomerNote = null;
    }

    if (updateData.administrativeDecisionStatusClient === "fullyAgreed") {
      updateData.administrativeDecisionFeedbackClient = "accept";
    }

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: updateData,
    });

    setIsLoadingInternal(false);
  };

  return (
    <LayoutAuthenticated>
      <HintBox headline={"Ihre Rückmeldung wird benötigt"}>{getHintText(product)}</HintBox>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <AdministrativeDecisionFeedbackForm product={product} isLoading={isLoadingInternal} />
      </FormProvider>
    </LayoutAuthenticated>
  );
};

export default AdministrativeDecisionFeedbackPage;
